import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useStoreInfo, useUpdateStore } from '../hooks/useSettings';
import {
  LOCATION_ADDRESS_MAX_LENGTH,
  LOCATION_CITY_MAX_LENGTH,
  LOCATION_COUNTRY_MAX_LENGTH,
  LOCATION_HOURS_MAX_LENGTH,
  LOCATION_NAME_MAX_LENGTH,
  MAX_STORE_LOCATIONS,
  StoreLocation,
} from '../services/settingsService';

const EMPTY_LOCATION: StoreLocation = {
  name: '',
  address: '',
  city: '',
  country: '',
  hours: '',
};

function fromStore(locations: StoreLocation[] | undefined): StoreLocation[] {
  if (!locations) return [];
  return locations.map((loc) => ({
    name: loc.name ?? '',
    address: loc.address ?? '',
    city: loc.city ?? '',
    country: loc.country ?? '',
    hours: loc.hours ?? '',
  }));
}

function trimLocation(loc: StoreLocation): StoreLocation {
  return {
    name: loc.name.trim(),
    address: loc.address.trim(),
    city: loc.city.trim(),
    country: loc.country.trim(),
    hours: loc.hours.trim(),
  };
}

function isLocationBlank(loc: StoreLocation): boolean {
  const trimmed = trimLocation(loc);
  return !(trimmed.name || trimmed.address || trimmed.city || trimmed.country || trimmed.hours);
}

function normalize(list: StoreLocation[]): StoreLocation[] {
  return list.map(trimLocation).filter((loc) => !isLocationBlank(loc));
}

export default function LocationsSettings() {
  const { data: store, isLoading } = useStoreInfo();
  const updateStore = useUpdateStore();

  const [draft, setDraft] = useState<StoreLocation[]>([]);
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    if (!hydrated && store) {
      setDraft(fromStore(store.locations));
      setHydrated(true);
    }
  }, [hydrated, store]);

  if (isLoading || !hydrated) {
    return <div className="py-8 text-center text-ink-3">Loading...</div>;
  }

  const saved = fromStore(store?.locations);
  const dirty = JSON.stringify(normalize(draft)) !== JSON.stringify(normalize(saved));

  const errors = draft.map((loc) => {
    const e: Partial<Record<keyof StoreLocation, string>> = {};
    if (loc.name.length > LOCATION_NAME_MAX_LENGTH) e.name = `Max ${LOCATION_NAME_MAX_LENGTH} chars.`;
    if (loc.address.length > LOCATION_ADDRESS_MAX_LENGTH) e.address = `Max ${LOCATION_ADDRESS_MAX_LENGTH} chars.`;
    if (loc.city.length > LOCATION_CITY_MAX_LENGTH) e.city = `Max ${LOCATION_CITY_MAX_LENGTH} chars.`;
    if (loc.country.length > LOCATION_COUNTRY_MAX_LENGTH) e.country = `Max ${LOCATION_COUNTRY_MAX_LENGTH} chars.`;
    if (loc.hours.length > LOCATION_HOURS_MAX_LENGTH) e.hours = `Max ${LOCATION_HOURS_MAX_LENGTH} chars.`;
    return e;
  });
  const hasErrors = errors.some((e) => Object.keys(e).length > 0);
  const atCap = draft.length >= MAX_STORE_LOCATIONS;

  const updateField = (idx: number, field: keyof StoreLocation, value: string) => {
    setDraft((prev) => prev.map((loc, i) => (i === idx ? { ...loc, [field]: value } : loc)));
  };

  const removeLocation = (idx: number) => {
    setDraft((prev) => prev.filter((_, i) => i !== idx));
  };

  const addLocation = () => {
    if (atCap) return;
    setDraft((prev) => [...prev, { ...EMPTY_LOCATION }]);
  };

  const handleSave = async () => {
    if (hasErrors) return;
    const cleaned = normalize(draft);
    try {
      await updateStore.mutateAsync({ locations: cleaned });
      // Reflect the trimmed/dropped state back into the form.
      setDraft(cleaned);
      toast.success(cleaned.length === 0 ? 'Locations cleared' : 'Locations saved');
    } catch {
      // hook already shows the toast
    }
  };

  return (
    <div className="space-y-6">
      <div className="rounded-panel border border-line bg-surface p-6 shadow-s">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="font-display text-lg font-semibold text-ink">Store Locations</h3>
            <p className="mt-1 text-sm text-ink-2">
              List your physical shops, warehouses, or pickup points. The agent will only confirm
              locations listed here and will say it doesn't have a record if asked about anywhere else.
            </p>
          </div>
          <span className="shrink-0 text-xs text-ink-3">
            {draft.length}/{MAX_STORE_LOCATIONS}
          </span>
        </div>

        {draft.length === 0 && (
          <div className="mt-4 rounded-card border border-dashed border-line bg-surface-2 p-6 text-center text-sm text-ink-3">
            No locations yet. Add one so the agent can answer questions like "Do you have a shop in Lagos?".
          </div>
        )}

        <div className="mt-4 space-y-4">
          {draft.map((loc, idx) => {
            const e = errors[idx] ?? {};
            return (
              <div
                key={idx}
                className="relative rounded-card border border-line bg-surface-2 p-4"
              >
                <button
                  type="button"
                  onClick={() => removeLocation(idx)}
                  className="absolute right-3 top-3 text-xs text-ink-3 hover:text-danger"
                  aria-label="Remove location"
                >
                  Remove
                </button>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label className="block text-xs font-medium text-ink-2">Name / label</label>
                    <input
                      type="text"
                      value={loc.name}
                      onChange={(ev) => updateField(idx, 'name', ev.target.value)}
                      placeholder="Lagos flagship"
                      className="mt-1 w-full rounded-xl border border-line bg-surface px-3 py-2 text-sm text-ink placeholder:text-ink-3 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
                    />
                    {e.name && <p className="mt-1 text-xs text-danger">{e.name}</p>}
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-ink-2">Country</label>
                    <input
                      type="text"
                      value={loc.country}
                      onChange={(ev) => updateField(idx, 'country', ev.target.value)}
                      placeholder="Nigeria"
                      className="mt-1 w-full rounded-xl border border-line bg-surface px-3 py-2 text-sm text-ink placeholder:text-ink-3 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
                    />
                    {e.country && <p className="mt-1 text-xs text-danger">{e.country}</p>}
                  </div>

                  <div className="sm:col-span-2">
                    <label className="block text-xs font-medium text-ink-2">Address</label>
                    <input
                      type="text"
                      value={loc.address}
                      onChange={(ev) => updateField(idx, 'address', ev.target.value)}
                      placeholder="12 Allen Ave, Ikeja"
                      className="mt-1 w-full rounded-xl border border-line bg-surface px-3 py-2 text-sm text-ink placeholder:text-ink-3 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
                    />
                    {e.address && <p className="mt-1 text-xs text-danger">{e.address}</p>}
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-ink-2">City</label>
                    <input
                      type="text"
                      value={loc.city}
                      onChange={(ev) => updateField(idx, 'city', ev.target.value)}
                      placeholder="Lagos"
                      className="mt-1 w-full rounded-xl border border-line bg-surface px-3 py-2 text-sm text-ink placeholder:text-ink-3 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
                    />
                    {e.city && <p className="mt-1 text-xs text-danger">{e.city}</p>}
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-ink-2">Hours (optional)</label>
                    <input
                      type="text"
                      value={loc.hours}
                      onChange={(ev) => updateField(idx, 'hours', ev.target.value)}
                      placeholder="Mon–Fri 9am–5pm WAT"
                      className="mt-1 w-full rounded-xl border border-line bg-surface px-3 py-2 text-sm text-ink placeholder:text-ink-3 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
                    />
                    {e.hours && <p className="mt-1 text-xs text-danger">{e.hours}</p>}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={addLocation}
            disabled={atCap}
            className="rounded-pill border border-line bg-surface-2 px-3 py-2 text-sm font-medium text-ink hover:bg-black/[0.04] disabled:cursor-not-allowed disabled:opacity-50"
          >
            + Add location
          </button>
          <button
            onClick={handleSave}
            disabled={!dirty || hasErrors || updateStore.isPending}
            className="rounded-pill bg-brand px-4 py-2 text-sm font-medium text-white hover:bg-brand/90 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {updateStore.isPending ? 'Saving...' : 'Save'}
          </button>
          <span className="text-xs text-ink-3">{dirty ? 'Unsaved changes' : 'Saved'}</span>
        </div>
      </div>
    </div>
  );
}
